import { Injectable, computed, signal } from '@angular/core';
import { isBffMode } from './auth';

export interface AuthIdentity {
  userId: string | null;
  email: string | null;
}

const NO_IDENTITY: AuthIdentity = { userId: null, email: null };

interface AuthTokenPayload {
  sub?: string;
  userId?: string;
  id?: string;
  email?: string;
}

function decodeLegacyToken(): AuthIdentity {
  try {
    const raw = localStorage.getItem('authState');
    if (!raw) return NO_IDENTITY;
    const token = (JSON.parse(raw) as { accessToken?: string }).accessToken;
    const payloadSegment = token?.split('.')[1];
    if (!payloadSegment) return NO_IDENTITY;
    const payload = JSON.parse(atob(payloadSegment.replace(/-/g, '+').replace(/_/g, '/'))) as AuthTokenPayload;
    const email = payload.email;
    return {
      userId: payload.sub ?? payload.userId ?? payload.id ?? null,
      email: typeof email === 'string' && email.length > 0 ? email : null,
    };
  } catch {
    return NO_IDENTITY;
  }
}

/**
 * Who the cabinet is signed in as. The BFF session cookie is opaque — the browser holds no JWT to
 * read — so under it the identity comes from the gateway's whoami, once per session; the legacy
 * token mode keeps decoding the token it does have.
 */
@Injectable({ providedIn: 'root' })
export class AuthIdentityService {
  private readonly identity = signal<AuthIdentity>(NO_IDENTITY);
  private inFlight: Promise<AuthIdentity> | null = null;

  readonly userId = computed(() => this.identity().userId);
  readonly email = computed(() => this.identity().email);

  /** Idempotent: concurrent callers share one whoami, and a resolved identity is kept. */
  load(): Promise<AuthIdentity> {
    if (!isBffMode()) {
      const decoded = decodeLegacyToken();
      this.identity.set(decoded);
      return Promise.resolve(decoded);
    }

    const known = this.identity();
    if (known.userId) return Promise.resolve(known);
    this.inFlight ??= this.whoami().finally(() => (this.inFlight = null));
    return this.inFlight;
  }

  // Bare fetch, not HttpClient, for the same reason the session lib uses one: the whoami must stay
  // outside the auth interceptors, whose own 401 handling asks this very endpoint.
  private async whoami(): Promise<AuthIdentity> {
    try {
      const res = await fetch('/api/auth/bff/session');
      const body = res.ok ? ((await res.json()) as { authenticated?: boolean; userId?: string; email?: string }) : null;
      const resolved: AuthIdentity = body?.authenticated
        ? { userId: body.userId ?? null, email: body.email ?? null }
        : NO_IDENTITY;
      this.identity.set(resolved);
      return resolved;
    } catch {
      // Degrade quietly: the live room is simply not joined and the menu shows no email line.
      // The next load() retries — nothing is cached from a failed answer.
      return NO_IDENTITY;
    }
  }
}
